import { Modal } from 'antd';
import type { CSSProperties, ReactNode } from 'react';

const HEADER_BG = '#0179C1';

type Props = {
  open: boolean;
  onCancel: () => void;
  title: ReactNode;
  /** Optional footer override. Most modals pass an array of <Button> elements. */
  footer?: ReactNode;
  /** Modal width. Accepts a number (px) OR a string ("90vw" for the inline
   *  preview modal, which wants viewport-relative sizing). Defaults to 560 px. */
  width?: number | string;
  /** Body styles merged on top of the default (24px padding). Use sparingly. */
  bodyStyle?: CSSProperties;
  /** Styles for the outer `.ant-modal-content` box — used by the fullscreen
   *  preview modal to flex-layout the body so a long form scrolls internally
   *  instead of pushing the modal off-viewport. Most modals leave this
   *  undefined. */
  contentStyle?: CSSProperties;
  /** Positioning override for the `.ant-modal` element. The fullscreen preview
   *  modal uses this to pin `top: 0` so the modal sits flush with the
   *  viewport rather than AntD's default 100 px offset. Most modals leave
   *  this undefined and get AntD's centered placement. */
  style?: CSSProperties;
  children: ReactNode;
};

/**
 * AntD Modal preset with the BMO design treatment: blue header band, white title +
 * close icon, consistent body/footer padding. Used by Create/Edit, Clone, Reject,
 * and Rejection-Details modals — single source of truth for the framed shell.
 *
 * Anything more specialised (the form layout inside, footer buttons, etc.) stays
 * with the consumer; this only owns the chrome.
 */
export function BmoModal({
  open,
  onCancel,
  title,
  footer,
  width = 560,
  bodyStyle,
  contentStyle,
  style,
  children,
}: Props) {
  return (
    <Modal
      open={open}
      onCancel={onCancel}
      // forceRender mounts the body on first render so consumers' `Form.useForm()`
      // instances connect immediately. Without it AntD warns that the form isn't
      // attached to any Form element until the modal first opens. State across
      // opens is reset by consumers via form.resetFields() in their close handlers.
      forceRender
      width={width}
      style={style}
      title={<span style={{ color: '#fff' }}>{title}</span>}
      closeIcon={<span style={{ color: '#fff', fontSize: 18 }}>×</span>}
      styles={{
        header: { background: HEADER_BG, margin: 0, padding: '16px 24px' },
        content: { padding: 0, overflow: 'hidden', ...contentStyle },
        body: { padding: 24, ...bodyStyle },
        footer: { padding: '12px 24px', margin: 0 },
      }}
      footer={footer}
    >
      {children}
    </Modal>
  );
}
